import * as React from 'react';
import classNames from 'classnames';
import { debounce, isEmpty } from 'lodash-es';
import * as Classes from '../common/classes';
import {
  BaseAutocomplete,
  BaseAutocompleteProps,
} from './BaseAutocomplete/BaseAutocomplete';
import { mcHistory, mcSearch } from '@mc/react-ui-icons';
import { InputAddons } from '../input-addons/InputAddons';
import { InputValueMode, IAddonsProps } from '../common/props';
import { ValueInputDetail } from '../value-input-detail/ValueInputDetail';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { List } from '../list/List';
import { ListItem } from '../list/ListItem';

export interface CancellableApiPromise<T> extends Promise<T> {
  cancel?: (reason?: string) => CancellableApiPromise<T>;
}

export type AutocompleteProps<API_TYPE, VAL_TYPE> = IAddonsProps &
  Omit<
    BaseAutocompleteProps<API_TYPE, VAL_TYPE> & {
      onSearchItems: (text: string) => CancellableApiPromise<API_TYPE[]>;
      onClearItems?: () => void;
      minSearchString?: number;
      customNoItemsFoundText?: string;
      debounceDelay?: number;
      initialItems?: API_TYPE[];
      latestItems?: API_TYPE[];
      /**
       * Pokud je zapnuto a není vybraný item, vrátí po stisknutí entru napsaný text. Nevolá se, pokud je povoleno allowCreateItems nebo tags.
       */
      onSearchByText?: (val: string) => void;
      valueMode?: InputValueMode;
      detailExtra?: (
        value: VAL_TYPE,
        onChange: ((val?: VAL_TYPE | undefined) => void) | undefined,
        disabled?: boolean
      ) => React.ReactNode;
    },
    | 'items'
    | 'touched'
    | 'error'
    | 'inline'
    | 'getDropDownIcon'
    | 'actions'
    | 'showMenuOnFocus'
    | 'alwaysExpanded'
    | 'tree'
    | 'noItemsFound'
    | 'useIdAsValue'
  >;

/**
 * Wrapper pro vytvoření cancellable api promise z předaného promise - pro možnost použití v api a jeho cancel.
 * @param promise promise
 * @param onCancel cancel callback
 */
export function cancellableApi<T>(
  promise: Promise<T>,
  onCancel?: () => void
): CancellableApiPromise<T> {
  let cancel: ((reason: string) => CancellableApiPromise<T>) | null = null;
  let cancellable: CancellableApiPromise<T>;
  cancellable = new Promise((resolve, reject) => {
    cancel = () => {
      try {
        if (onCancel) {
          onCancel();
        }
      } catch (e) {
        reject(e);
      }
      return cancellable;
    };
    promise.then(resolve, reject);
  });
  if (cancel) {
    cancellable.cancel = cancel;
  }
  return cancellable;
}

/**
 * API autocomplete.
 * Zatím nepodporuje debounce.
 */

export function Autocomplete<API_TYPE, VAL_TYPE = API_TYPE>({
  minSearchString = 1,
  customNoItemsFoundText,
  initialItems = [],
  latestItems = [],
  debounceDelay = 500,
  onSearchItems,
  onTextChange,
  onClearItems,
  className,
  allowCreateItems,
  addonAfter,
  addonAfterCompact,
  inputProps,
  disabled,
  readOnly,
  valueMode,
  renderItemContent,
  getItemRenderClass,
  getItemName,
  detailExtra,
  dataTest,
  ...other
}: AutocompleteProps<API_TYPE, VAL_TYPE>) {
  const [items, setItems] = React.useState(initialItems);
  const [searchText, setText] = React.useState('');
  const [searching, setSearching] = React.useState<boolean>(false);

  const currentApiSearch =
    React.useRef<CancellableApiPromise<API_TYPE[]>>(null);
  const callSearch = React.useRef<boolean>(false);
  const currentSearchText = React.useRef<String>('');

  const search = React.useCallback(
    debounce((text: any) => {
      if (callSearch.current) {
        setSearching(true);

        if (currentApiSearch.current && currentApiSearch.current.cancel) {
          currentApiSearch.current.cancel();
        }

        let call = onSearchItems(text);
        // @ts-ignore
        currentApiSearch.current = call;

        call
          .then((items) => {
            if (
              currentApiSearch.current === call &&
              currentSearchText.current == text
            ) {
              // @ts-ignore
              currentApiSearch.current = null;
              setItems(items);
            }
          })
          .finally(() => {
            setSearching(false);
          });
      }
    }, debounceDelay),
    [debounceDelay, onSearchItems]
  );

  const useMinSearchString = minSearchString || 1;

  let noItemsFoundText: string;
  if (searching) {
    noItemsFoundText = 'Probíhá vyhledávání...';
  } else {
    if (searchText && searchText.length >= useMinSearchString) {
      noItemsFoundText = 'Pro zadaný řetězec nebyly nalezeny žádné záznamy';
    } else {
      if (customNoItemsFoundText) {
        noItemsFoundText = customNoItemsFoundText;
      } else if (useMinSearchString == 1) {
        noItemsFoundText = 'Pro vyhledání zadejte text (alespoň jeden znak)';
      } else if (useMinSearchString <= 3) {
        noItemsFoundText = `Pro vyhledání zadejte text (alespoň ${useMinSearchString} znaky)`;
      } else {
        noItemsFoundText = `Pro vyhledání zadejte text (alespoň ${useMinSearchString} znaků)`;
      }
    }
  }

  return other?.value && valueMode === 'detail' ? (
    <ValueInputDetail<API_TYPE, VAL_TYPE>
      value={other.value}
      onChange={other.onChange}
      renderItemContent={renderItemContent}
      getItemName={getItemName}
      renderExtra={detailExtra}
      disabled={disabled}
      readOnly={readOnly}
    />
  ) : (
    <InputAddons
      addonAfter={
        isEmpty(latestItems) ? (
          addonAfter
        ) : (
          <ButtonToolbar>
            <Button
              icon={<Icon icon={mcHistory} />}
              disabled={disabled || readOnly}
              menu={
                <List
                  mode={'input'}
                  className={Classes.AUTOCOMPLETE_LATEST_ITEMS}
                >
                  {latestItems?.map((item, id) => {
                    // @ts-ignore
                    const name = getItemName ? getItemName(item) : item?.name;
                    // @ts-ignore
                    return (
                      // @ts-ignore
                      <ListItem key={id} onClick={() => other.onChange?.(item)}>
                        {renderItemContent ? (
                          renderItemContent(item, name)
                        ) : (
                          <span className="item-text">{name}</span>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              }
              popupProps={{ position: 'bottom right' }}
            />
            {addonAfter}
          </ButtonToolbar>
        )
      }
      addonAfterCompact={addonAfterCompact}
    >
      <BaseAutocomplete<API_TYPE, VAL_TYPE>
        dataTest={dataTest}
        allowCreateItems={allowCreateItems}
        useAutocompleteInternalFilter={false}
        className={classNames(
          className,
          Classes.BASE_AUTOCOMPLETE,
          Classes.AUTOCOMPLETE,
          {
            ['searching']: !allowCreateItems && searching,
            [Classes.FIELD_INPUT_DISABLED]: disabled,
            [Classes.FIELD_INPUT_READONLY]: readOnly,
          }
        )}
        inputProps={{ className: Classes.FIELD_INPUT, ...inputProps }}
        noItemsFound={noItemsFoundText}
        shouldItemRender={(_, __) => true}
        onCloseMenu={() => {
          if (other.value === null || other.value === undefined) {
            setText('');
            setItems([]);
          }
        }}
        showMenuOnFocus={false}
        // @ts-ignore
        dropDownIconClickOpenMenu={false}
        getDropDownIcon={() => mcSearch}
        hasDropdownIcon={!allowCreateItems}
        {...other}
        renderItemContent={renderItemContent}
        getItemRenderClass={(item) => {
          const defaultItemContentClass =
            valueMode === 'detail' && !!renderItemContent
              ? 'item-content-padding'
              : '';
          return getItemRenderClass
            ? getItemRenderClass(item)
            : defaultItemContentClass;
        }}
        getItemName={getItemName}
        disabled={disabled}
        readOnly={readOnly}
        items={searching ? [] : items}
        onTextChange={(text) => {
          setText(text);
          currentSearchText.current = text;
          if (useMinSearchString <= text.length) {
            setSearching(true);

            setItems([]);
            onClearItems && onClearItems();

            callSearch.current = true;
            search(text);
          } else {
            if (currentApiSearch.current && currentApiSearch.current.cancel) {
              currentApiSearch.current.cancel();
            }
            // @ts-ignore
            currentApiSearch.current = null;
            callSearch.current = false;
            if (searching) {
              setSearching(false);
            }

            if (items && items.length > 0) {
              setItems([]);
              onClearItems && onClearItems();
            }
          }
        }}
      />
    </InputAddons>
  );
}
